import React, { Fragment, MouseEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Download, Calendar, RefreshCw, ClipboardCheck } from "lucide-react";

import { useReportSSE } from '../../../hooks/analyses';
import { useSaveV2Feedback } from "../../../hooks/v2_feedback";
import { useGetConversationsForSimulationReport } from "../../../hooks/conversations";

import { ESimulationClient, ESimulationKey } from '../../../types/entities.enum';
import { ISimulationReportV1, ISimulationReportV2 } from "../../../types/services.interface";

import { AnalysisV1 } from "./V1";
import { AnalysisV2 } from "./V2";
import { LogoPackageHoriz } from "../../sub/LogoPackage";

export const Analysis = () => {
    const { simulationKey } = useParams();

    // eslint-disable-next-line
    const { currentStep, reportResponse, isLoading, isError } = useReportSSE(simulationKey ? simulationKey as ESimulationKey : null);
    const { data: conversations } = useGetConversationsForSimulationReport(simulationKey ? simulationKey as ESimulationKey : null);

    return (
        <div className='analysis__page'>
            {isLoading && !reportResponse?.report && !isError && (
                <Loading />
            )}
            {!isLoading && !!reportResponse?.report && !isError && (
                <Fragment>
                    {reportResponse.version === 'v1' && <AnalysisV1 report={reportResponse.report as ISimulationReportV1} />}
                    {reportResponse.version === 'v2' &&
                        <AnalysisV2
                            report={reportResponse.report as ISimulationReportV2}
                            conversations={conversations ?? []}
                            feedback={<Feedback simulationKey={simulationKey ? simulationKey as ESimulationKey : null} />}
                            footer={<Footer report={reportResponse.report as ISimulationReportV2} />}
                        />
                    }
                </Fragment>
            )}
        </div>
    );
};

const Loading = () => {
    const topLineTexts = [
        "Reviewing how you approached each scenario…",
        "Identifying patterns in how you make decisions.",
        "Bringing those patterns together into personal insights.",
        "Preparing a clear view of your leadership tendencies.",
    ];

    const containerRef = useRef<HTMLDivElement>(null);

    const [currentTextIdx, setCurrentText] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleAnimationIteration = () => {
            setCurrentText(prev => (prev + 1) % topLineTexts.length);
        };

        el.addEventListener("animationiteration", handleAnimationIteration);

        return () => {
            el.removeEventListener("animationiteration", handleAnimationIteration);
        };

        // eslint-disable-next-line
    }, []);

    return (
        <Fragment>

            <div className='analysis__loading'>
                <p className='text-lg mb-5xl text-center'>
                    Our analysis engine is at work. This may take up to 2 minutes 😄<br />
                    <span className="color--gray-500">If you see a blank screen, please refresh the page and wait for results.</span>
                </p>
                <div className="analysis__loading__fading_sec" ref={containerRef}>
                    <p className='analysis__loading__top_line'>
                        {topLineTexts[currentTextIdx]}
                    </p>
                    <div className='analysis__loading__divider' />
                    <p className="analysis__loading__bottom_line">
                        This simulation looks at patterns, not isolated responses.
                    </p>
                </div>
            </div>
        </Fragment>
    );
};

const Feedback = ({ simulationKey }: { simulationKey: ESimulationKey | null }) => {

    const navigate = useNavigate();

    const questions: { key: string; title: string; legendStart: string; legendEnd: string; }[] = [
        {
            key: 'insight_clarity',
            title: 'How clear and actionable were the insights provided in this report?',
            legendStart: 'Not Clear',
            legendEnd: 'Very Clear',
        },
        {
            key: 'visualization_helpfulness',
            title: 'How well did the visual data representations help you understand your performance?',
            legendStart: 'Not Helpful',
            legendEnd: 'Very Helpful',
        },
        {
            key: 'recommendation_likelihood',
            title: 'How likely are you to recommend this assessment to colleagues?',
            legendStart: 'Not Likely',
            legendEnd: 'Very Likely',
        },
    ];

    const [responses, setResponses] = useState<{ question: string; rating: number }[]>([]);
    const [improvement, setImprovement] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const { mutate: save } = useSaveV2Feedback();

    const isSubmitDisabled = () => {
        return isProcessing || isSuccess || responses.length !== questions.length;
    };

    // const isImprovementVisible = () => {
    //     return responses.some(r => r.rating <= 3);
    // };

    const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (isSubmitDisabled()) return;

        save({
            payload: {
                responses,
                // improvement: isImprovementVisible() && improvement.length ? improvement : null,
                improvement: improvement.length ? improvement : null,
            },
            setIsProcessing,
            setIsSuccess,
        });
    };

    const handleOpenCalender = () => {
        if (window.location.origin.startsWith(`https://${ESimulationClient.RIDLEY_CONSULTING}`)) {
            return window.open('https://calendly.com/bethridley1/30min', '_blank');
        };

        window.open('https://calendly.com/mindshield/30min', '_blank');
    };

    return (
        <div className='analysis__feedback'>
            <div className="analysis__feedback__questions">
                <p className='text-sm tracking-wider text-bold mb-2xl'>YOUR FEEDBACK</p>
                {!isSuccess &&
                    <Fragment>
                        <div className="analysis__feedback__questions__list">
                            {questions.map((que, idx) => (
                                <div key={que.key} className="analysis__feedback__question">
                                    <p className="text-sm text-semibold">{idx + 1}. {que.title}</p>
                                    <div className="analysis__feedback__question__radio_btns">
                                        {Array.from({ length: 5 }).map((_, idx) => {
                                            const qIdx = responses.findIndex(r => r.question === que.title);

                                            return (
                                                <label
                                                    key={`${que.key}_${idx}`}
                                                    htmlFor={`${que.key}_${idx}`}
                                                    className="analysis__feedback__question__radio_btns__btn"
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`feedback_radio--${que.key}`}
                                                        id={`${que.key}_${idx}`}
                                                        checked={qIdx !== -1 && responses[qIdx].rating === idx + 1}
                                                        onChange={() => setResponses(prev => {
                                                            let newList = [...prev];
                                                            if (qIdx !== -1) {
                                                                newList[qIdx].rating = idx + 1;
                                                            } else {
                                                                newList.push({ question: que.title, rating: idx + 1 });
                                                            };
                                                            return newList;
                                                        })}
                                                    />
                                                    <p>{idx + 1}</p>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <div className="analysis__feedback__question__radio_legend">
                                        <p>{que.legendStart}</p>
                                        <p>{que.legendEnd}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* {isImprovementVisible() && ( */}
                        <div className="analysis__feedback__questions__improvement">
                            <p className="text-sm text-medium color--gray-600 mb-xs">How could we improve this experience? (optional)</p>
                            <textarea
                                rows={4}
                                placeholder="Share anything that felt unclear, missing, or could work better."
                                value={improvement}
                                onChange={(e) => setImprovement(e.target.value)}
                            />
                        </div>
                        {/* )} */}
                        <button
                            className='btn btn--fluid btn--md btn--primary'
                            disabled={isSubmitDisabled()}
                            onClick={onSubmit}
                        >
                            {isProcessing && <span className='btn-loader' />}
                            {isProcessing ? <span>Submitting..</span> : <span>Submit Feedback</span>}
                        </button>
                    </Fragment>
                }
                {isSuccess &&
                    <div className="analysis__feedback__success">
                        <ClipboardCheck />
                        <p>Thanks for your feedback!</p>
                        <p>Your feedback has been recorded successfully.</p>
                    </div>
                }
            </div>
            <div className="analysis__feedback__actions">
                <p className='text-sm tracking-wider text-bold mb-2xl'>NEXT STEPS</p>
                <div className="analysis__feedback__action_btns">
                    <button
                        className="analysis__feedback__action_btns__btn analysis__feedback__action_btns__btn--download"
                        onClick={() => window.print()}
                    >
                        <div className="analysis__feedback__action_btns__btn__icon">
                            <Download />
                        </div>
                        <div className="analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--white">Download Report</p>
                            <p className="text-sm color--white">Save a PDF copy of your performance report for your records or to share with your team.</p>
                        </div>
                    </button>
                    <button
                        className="analysis__feedback__action_btns__btn analysis__feedback__action_btns__btn--retake"
                        onClick={() => window.location.origin.startsWith(`https://${ESimulationClient.RIDLEY_CONSULTING}`) ? null : navigate(`/simulation/${simulationKey}/stages`, { replace: true })}
                        disabled={window.location.origin.startsWith(`https://${ESimulationClient.RIDLEY_CONSULTING}`)}
                    >
                        <div className="analysis__feedback__action_btns__btn__icon">
                            <RefreshCw />
                        </div>
                        <div className="analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--primary">Retake Assessment</p>
                            <p className="text-sm color--primary">Explore the simulation again to deepen your insights and track your progress over time.</p>
                        </div>
                    </button>
                    <button
                        className="analysis__feedback__action_btns__btn analysis__feedback__action_btns__btn--book"
                        onClick={handleOpenCalender}
                    >
                        <div className="analysis__feedback__action_btns__btn__icon">
                            <Calendar />
                        </div>
                        <div className="analysis__feedback__action_btns__btn__right">
                            <p className="text-sm text-bold color--primary">Book a Call</p>
                            <p className="text-sm color--primary">Schedule an exploratory conversation to bring curated leadership simulations to your organization.</p>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

const Footer = ({ report }: { report: ISimulationReportV2 }) => {
    return (
        <div className="analysis__footer">
            <div className="analysis__footer__top">
                <LogoPackageHoriz />
                <p className="text-xs">Mid-Level Leadership Dashboard</p>
                <p className="text-xs">
                    <strong>
                        {report.current_role} ({report.current_level}) | {report.milestone_role} ({report.milestone_level})
                    </strong>
                </p>
            </div>
            <div className="analysis__footer__bottom">
                <p>Report by</p>
                <Link className="link link--primary" to='https://www.mindshield.org/' target="_blank">MindShield</Link>
            </div>
        </div>
    );
};